import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, X, Loader2, Star, ImagePlus, QrCode, FileDown, Settings2, Check, Megaphone, Store, ExternalLink } from "lucide-react";
import { formatBRL } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { nome: "", marca: "", ano: 2024, km: 0, preco: 0, cambio: "", combustivel: "", categoria: "SUVs", imagem: "", fotos: [], destaque: false, status: "disponivel", preco_custo: "", para_locacao: false, diaria: "" };

const STATUS_VEICULO = {
  disponivel: { label: "Disponível", cls: "bg-emerald-500/15 text-emerald-400" },
  reservado: { label: "Reservado", cls: "bg-amber-500/15 text-amber-400" },
  vendido: { label: "Vendido", cls: "bg-slate-500/15 text-slate-400" },
};

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

export const VehiclesTab = ({ onUnauthorized, role = "admin", onGerarAnuncio }) => {
  const isAdmin = role === "admin";
  const [vehicles, setVehicles] = useState(null);
  const [custosMap, setCustosMap] = useState({});
  const [form, setForm] = useState(null);
  const [qrFor, setQrFor] = useState(null);
  const [categorias, setCategorias] = useState(["SUVs", "Esportivos", "Sedans"]);
  const [catManager, setCatManager] = useState(false);
  const [novaCat, setNovaCat] = useState("");
  const [editCat, setEditCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [mlFor, setMlFor] = useState(null);
  const [mlForm, setMlForm] = useState(null);
  const [mlSaving, setMlSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const openMl = (v) => {
    const modelo = v.nome.replace(v.marca, "").trim();
    setMlFor(v);
    setMlForm({
      titulo: v.nome.slice(0, 60), preco: v.preco, marca: v.marca, modelo,
      versao: "", portas: 4, ano: v.ano, km: v.km, combustivel: v.combustivel || "", placa: "",
      exposicao: "silver",
    });
  };

  const publishMl = async (e) => {
    e.preventDefault();
    setMlSaving(true);
    try {
      const res = await axios.post(`${API}/ml/publish`, { ...mlForm, vehicle_id: mlFor.id }, { headers });
      toast.success("Anúncio criado no Mercado Livre!");
      if (res.data?.permalink) window.open(res.data.permalink, "_blank");
      setMlFor(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else if (err.response?.status === 409) toast.error(typeof err.response.data.detail === "string" ? err.response.data.detail : "Conecte a conta do Mercado Livre em Conta > Integrações.");
      else {
        const d = err.response?.data?.detail;
        const causes = d?.ml_body?.cause?.map((c) => c.message).filter(Boolean);
        const mlMsg = causes?.length ? causes.join(" | ") : (d?.ml_body?.message || d?.ml_body?.error);
        toast.error(mlMsg ? `Mercado Livre: ${mlMsg}` : (typeof d === "string" ? d : "Erro ao publicar no Mercado Livre."));
      }
    } finally { setMlSaving(false); }
  };

  const load = useCallback(async () => {
    try {
      const [res, cats] = await Promise.all([
        axios.get(`${API}/vehicles/admin`, { headers }),
        axios.get(`${API}/config/categorias`),
      ]);
      let custos = { data: [] };
      if (isAdmin) {
        try {
          custos = await axios.get(`${API}/custos`, { headers });
        } catch { custos = { data: [] }; }
      }
      setVehicles(res.data);
      setCustosMap(Object.fromEntries(custos.data.map((i) => [i.veiculo.id, i])));
      if (cats.data.categorias?.length) setCategorias(cats.data.categorias);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else { setVehicles([]); toast.error("Erro ao carregar estoque."); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const todasCategorias = [...new Set([...categorias, ...(vehicles || []).map((v) => v.categoria).filter(Boolean)])];

  const addCategoria = async () => {
    const nome = novaCat.trim();
    if (!nome) return;
    if (todasCategorias.includes(nome)) return toast.error("Essa categoria já existe.");
    try {
      const res = await axios.put(`${API}/config/categorias`, { categorias: [...categorias, nome] }, { headers });
      setCategorias(res.data.categorias);
      setNovaCat("");
      toast.success(`Categoria "${nome}" criada.`);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao criar categoria.");
    }
  };

  const renomearCategoria = async () => {
    const novo = editCat?.para?.trim();
    if (!novo || novo === editCat.de) { setEditCat(null); return; }
    try {
      const res = await axios.post(`${API}/config/categorias/renomear`, { de: editCat.de, para: novo }, { headers });
      setCategorias(res.data.categorias);
      if (res.data.veiculos_atualizados > 0) {
        setVehicles((vs) => vs.map((v) => (v.categoria === editCat.de ? { ...v, categoria: novo } : v)));
        toast.success(`Categoria renomeada — ${res.data.veiculos_atualizados} carro(s) atualizados.`);
      } else {
        toast.success("Categoria renomeada.");
      }
      setEditCat(null);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(err.response?.data?.detail || "Erro ao renomear.");
    }
  };

  const removerCategoria = async (nome) => {
    const emUso = (vehicles || []).filter((v) => v.categoria === nome).length;
    if (emUso > 0) return toast.error(`"${nome}" está em ${emUso} carro(s). Renomeie ou troque a categoria deles antes.`);
    try {
      const res = await axios.put(`${API}/config/categorias`, { categorias: categorias.filter((c) => c !== nome) }, { headers });
      setCategorias(res.data.categorias);
      toast.success("Categoria removida.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao remover categoria.");
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/upload`, fd, { headers });
        const url = res.data.url;
        setForm((prev) => {
          const fotos = [...(prev.fotos || []), url];
          return { ...prev, fotos, imagem: prev.imagem || url };
        });
      }
      toast.success(files.length > 1 ? "Fotos enviadas!" : "Foto enviada!");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(err.response?.data?.detail || "Erro no upload da foto.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeFoto = (url) => {
    setForm((prev) => {
      const fotos = (prev.fotos || []).filter((f) => f !== url);
      return { ...prev, fotos, imagem: prev.imagem === url ? (fotos[0] || "") : prev.imagem };
    });
  };

  const setCover = (url) => setForm((prev) => ({ ...prev, imagem: url }));

  const save = async (e) => {
    e.preventDefault();
    if (!(form.fotos || []).length && !form.imagem) return toast.error("Envie ao menos uma foto do veículo.");
    setSaving(true);
    const payload = {
      ...form,
      ano: Number(form.ano),
      km: Number(form.km),
      preco: Number(form.preco),
      preco_custo: form.preco_custo ? Number(form.preco_custo) : null,
      diaria: form.diaria ? Number(form.diaria) : null,
    };
    delete payload.id;
    try {
      if (form.id) {
        await axios.put(`${API}/vehicles/${form.id}`, payload, { headers });
        toast.success("Veículo atualizado.");
      } else {
        await axios.post(`${API}/vehicles`, payload, { headers });
        toast.success("Veículo cadastrado.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao salvar veículo.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/vehicles/${id}`, { headers });
      setVehicles((vs) => vs.filter((v) => v.id !== id));
      toast.success("Veículo removido.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao remover veículo.");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold text-white">Estoque de veículos</h1>
        <button
          onClick={() => setForm({ ...empty, categoria: todasCategorias[0] || "SUVs" })} data-testid="vehicle-new-button"
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-5 py-2.5 rounded-full transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo veículo
        </button>
      </div>

      <AnimatePresence>
        {form && (
          <motion.form
            onSubmit={save}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="glass rounded-3xl p-6 sm:p-8 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="vehicle-form"
          >
            <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-between">
              <h2 className="font-display font-bold text-white">{form.id ? "Editar veículo" : "Novo veículo"}</h2>
              <button type="button" onClick={() => setForm(null)} data-testid="vehicle-form-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="sm:col-span-2 lg:col-span-3">
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="vehicle-file-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="vehicle-upload-button"
                className="w-full border-2 border-dashed border-slate-700 hover:border-emerald-500/60 rounded-2xl py-8 flex flex-col items-center gap-2 text-slate-400 hover:text-emerald-400 transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <ImagePlus className="w-6 h-6" />}
                <span className="text-sm font-semibold">
                  {uploading ? "Enviando fotos..." : "Enviar fotos do celular/computador (várias de uma vez)"}
                </span>
              </button>
              {(form.fotos || []).length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="vehicle-fotos-preview">
                  {form.fotos.map((f) => (
                    <div key={f} className={`relative rounded-xl overflow-hidden aspect-[4/3] border-2 ${form.imagem === f ? "border-emerald-500" : "border-transparent"}`}>
                      <img src={f} alt="foto" className="w-full h-full object-cover" />
                      {form.imagem === f && (
                        <span className="absolute bottom-1 left-1 bg-emerald-500 text-emerald-950 text-[10px] font-bold px-2 py-0.5 rounded-full">CAPA</span>
                      )}
                      <div className="absolute top-1 right-1 flex gap-1">
                        {form.imagem !== f && (
                          <button type="button" onClick={() => setCover(f)} title="Definir como capa" className="w-6 h-6 rounded-full bg-black/60 text-emerald-400 text-xs flex items-center justify-center">
                            <Star className="w-3 h-3" />
                          </button>
                        )}
                        <button type="button" onClick={() => removeFoto(f)} title="Remover" className="w-6 h-6 rounded-full bg-black/60 text-red-400 flex items-center justify-center">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <input className={inputCls} placeholder="Nome (ex: BMW X6 M60i)*" required value={form.nome} onChange={set("nome")} data-testid="vehicle-input-nome" />
            <input className={inputCls} placeholder="Marca*" required value={form.marca} onChange={set("marca")} data-testid="vehicle-input-marca" />
            <div>
              <label className="text-xs text-slate-400 mb-1.5 px-1 flex items-center justify-between">
                Categoria*
                <button type="button" onClick={() => { setCatManager(true); setNovaCat(""); setEditCat(null); }} data-testid="categorias-manage-button" className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 transition-colors">
                  <Settings2 className="w-3.5 h-3.5" /> Gerenciar
                </button>
              </label>
              <select className={inputCls} value={form.categoria} onChange={set("categoria")} data-testid="vehicle-input-categoria">
                {todasCategorias.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <input className={inputCls} type="number" placeholder="Ano*" required value={form.ano} onChange={set("ano")} data-testid="vehicle-input-ano" />
            <div>
              <label className="text-xs text-slate-400 block mb-1.5 px-1">Quilometragem (km rodados)* <span className="text-slate-600">— ex: 45.000 para 45 mil km</span></label>
              <input className={inputCls} type="number" min="0" placeholder="Ex: 45.000" required value={form.km} onChange={set("km")} data-testid="vehicle-input-km" />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1.5 px-1">Preço de venda (R$)* <span className="text-emerald-500/80">— valor anunciado no site</span></label>
              <input className={inputCls} type="number" placeholder="Ex: 330.000" required value={form.preco} onChange={set("preco")} data-testid="vehicle-input-preco" />
            </div>
            {isAdmin && (
            <div>
              <label className="text-xs text-slate-400 block mb-1.5 px-1">Preço de custo (R$) <span className="text-slate-600">— quanto você pagou no carro (opcional)</span></label>
              <input className={inputCls} type="number" placeholder="Ex: 280.000" value={form.preco_custo ?? ""} onChange={set("preco_custo")} data-testid="vehicle-input-custo" />
            </div>
            )}
            <p className="text-[11px] text-slate-500 px-2 flex items-center">Gastos do carro são lançados no Financeiro (categoria "Veículo")</p>
            <select className={inputCls} value={form.status || "disponivel"} onChange={set("status")} data-testid="vehicle-input-status">
              <option value="disponivel">Disponível</option>
              <option value="reservado">Reservado</option>
              <option value="vendido">Vendido (some da vitrine)</option>
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-300 px-2">
              <input type="checkbox" checked={!!form.para_locacao} onChange={set("para_locacao")} data-testid="vehicle-input-locacao" className="accent-emerald-500 w-4 h-4" />
              Disponível para locação
            </label>
            {form.para_locacao && (
              <input className={inputCls} type="number" placeholder="Diária de locação (R$)" value={form.diaria ?? ""} onChange={set("diaria")} data-testid="vehicle-input-diaria" />
            )}
            <input className={inputCls} placeholder="Câmbio (ex: Automático)*" required value={form.cambio} onChange={set("cambio")} data-testid="vehicle-input-cambio" />
            <input className={inputCls} placeholder="Combustível (ex: Gasolina)*" required value={form.combustivel} onChange={set("combustivel")} data-testid="vehicle-input-combustivel" />
            <label className="flex items-center gap-2 text-sm text-slate-300 px-2">
              <input type="checkbox" checked={form.destaque} onChange={set("destaque")} data-testid="vehicle-input-destaque" className="accent-emerald-500 w-4 h-4" />
              Destaque na vitrine
            </label>
            <input className={`${inputCls} sm:col-span-2 lg:col-span-3`} placeholder="URL da foto (opcional se já enviou fotos acima)" value={form.imagem} onChange={set("imagem")} data-testid="vehicle-input-imagem" />
            <div className="sm:col-span-2 lg:col-span-3 flex gap-3">
              <button
                type="submit" disabled={saving} data-testid="vehicle-save-button"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-3 rounded-full transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {form.id ? "Salvar alterações" : "Cadastrar veículo"}
              </button>
              <button type="button" onClick={() => setForm(null)} className="glass text-white text-sm font-semibold px-6 py-3 rounded-full">Cancelar</button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {vehicles === null ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-[#131620] animate-pulse" />)}</div>
      ) : vehicles.length === 0 ? (
        <p className="text-slate-400 text-sm glass rounded-2xl p-8" data-testid="vehicles-empty">Estoque vazio. Cadastre o primeiro veículo.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-vehicles-list">
          {vehicles.map((v) => {
            const st = STATUS_VEICULO[v.status || "disponivel"];
            const margem = custosMap[v.id] ? custosMap[v.id].lucro_previsto : null;
            return (
              <div key={v.id} className="glass rounded-2xl p-4 flex items-center gap-4" data-testid={`admin-vehicle-${v.id}`}>
                <img src={v.imagem} alt={v.nome} className="w-24 h-16 object-cover rounded-xl shrink-0 hidden sm:block" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white flex items-center gap-2 flex-wrap">
                    {v.nome}
                    {v.destaque && <Star className="w-3.5 h-3.5 fill-emerald-400 text-emerald-400" />}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`vehicle-status-${v.id}`}>{st.label}</span>
                  </p>
                  <p className="text-sm text-slate-400 truncate">
                    {v.marca} · {v.ano} · {v.categoria} · <span className="text-emerald-400 font-semibold">{formatBRL(v.preco)}</span>
                    {margem !== null && <span className={margem >= 0 ? "text-emerald-500" : "text-red-400"}> · lucro prev. {formatBRL(margem)}</span>}
                    {(v.fotos || []).length > 0 && <span className="text-slate-500"> · {v.fotos.length} foto(s)</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {v.ml_item_id ? (
                    <a
                      href={v.ml_permalink || "#"} target="_blank" rel="noopener noreferrer"
                      data-testid={`vehicle-ml-link-${v.id}`}
                      className="w-10 h-10 rounded-full bg-yellow-400/15 flex items-center justify-center text-yellow-400 hover:bg-yellow-400/30 transition-colors"
                      aria-label="Publicado no Mercado Livre" title={`Publicado no Mercado Livre (${v.ml_item_id})`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ) : v.status !== "vendido" && (
                    <button
                      onClick={() => openMl(v)} data-testid={`vehicle-ml-publish-${v.id}`}
                      className="w-10 h-10 rounded-full bg-yellow-400/15 flex items-center justify-center text-yellow-400 hover:bg-yellow-400/30 transition-colors"
                      aria-label="Publicar no Mercado Livre" title="Publicar no Mercado Livre"
                    >
                      <Store className="w-4 h-4" />
                    </button>
                  )}
                  {onGerarAnuncio && (
                    <button
                      onClick={() => onGerarAnuncio(v)} data-testid={`vehicle-anuncio-${v.id}`}
                      className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                      aria-label="Gerar anúncio" title="Gerar anúncio com esta foto e preço"
                    >
                      <Megaphone className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setQrFor(v)} data-testid={`vehicle-qr-${v.id}`}
                    className="w-10 h-10 rounded-full bg-sky-500/15 flex items-center justify-center text-sky-400 hover:bg-sky-500/30 transition-colors"
                    aria-label="QR Code do anúncio"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setForm({ ...empty, ...v, fotos: v.fotos || [], preco_custo: v.preco_custo ?? "" })} data-testid={`vehicle-edit-${v.id}`}
                    className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors"
                    aria-label="Editar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(v.id)} data-testid={`vehicle-delete-${v.id}`}
                    className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors"
                    aria-label="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {mlFor && mlForm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !mlSaving && setMlFor(null)}
            data-testid="ml-publish-modal"
          >
            <motion.form
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={publishMl}
              className="glass rounded-3xl p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold text-white flex items-center gap-2">
                    <Store className="w-5 h-5 text-yellow-400" /> Publicar no Mercado Livre
                  </h2>
                  <p className="text-xs text-slate-400 mt-1">Revise os dados do anúncio (categoria: Carros e Caminhonetes).</p>
                </div>
                <button type="button" onClick={() => setMlFor(null)} data-testid="ml-modal-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <input className={inputCls} required maxLength={60} placeholder="Título do anúncio (máx. 60)*" value={mlForm.titulo} onChange={(e) => setMlForm({ ...mlForm, titulo: e.target.value })} data-testid="ml-input-titulo" />
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} required type="number" min="0" step="any" placeholder="Preço (R$)*" value={mlForm.preco} onChange={(e) => setMlForm({ ...mlForm, preco: Number(e.target.value) })} data-testid="ml-input-preco" />
                <input className={inputCls} required placeholder="Marca*" value={mlForm.marca} onChange={(e) => setMlForm({ ...mlForm, marca: e.target.value })} data-testid="ml-input-marca" />
                <input className={inputCls} required placeholder="Modelo* (ex: 911 Carrera)" value={mlForm.modelo} onChange={(e) => setMlForm({ ...mlForm, modelo: e.target.value })} data-testid="ml-input-modelo" />
                <input className={inputCls} placeholder="Versão (ex: 1.0 LTZ)" value={mlForm.versao} onChange={(e) => setMlForm({ ...mlForm, versao: e.target.value })} data-testid="ml-input-versao" />
                <input className={inputCls} required type="number" min="1970" max="2030" placeholder="Ano*" value={mlForm.ano} onChange={(e) => setMlForm({ ...mlForm, ano: Number(e.target.value) })} data-testid="ml-input-ano" />
                <input className={inputCls} required type="number" min="0" placeholder="KM*" value={mlForm.km} onChange={(e) => setMlForm({ ...mlForm, km: Number(e.target.value) })} data-testid="ml-input-km" />
                <input className={inputCls} placeholder="Combustível (ex: Flex)" value={mlForm.combustivel} onChange={(e) => setMlForm({ ...mlForm, combustivel: e.target.value })} data-testid="ml-input-combustivel" />
                <input className={inputCls} placeholder="Placa (opcional)" maxLength={8} value={mlForm.placa} onChange={(e) => setMlForm({ ...mlForm, placa: e.target.value.toUpperCase() })} data-testid="ml-input-placa" />
                <select className={inputCls} value={mlForm.portas} onChange={(e) => setMlForm({ ...mlForm, portas: Number(e.target.value) })} data-testid="ml-input-portas">
                  {[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} portas</option>)}
                </select>
                <select className={inputCls} value={mlForm.exposicao} onChange={(e) => setMlForm({ ...mlForm, exposicao: e.target.value })} data-testid="ml-input-exposicao" title="Nível de exposição — cobrança feita pelo Mercado Livre no cartão cadastrado">
                  <option value="silver">Exposição Prata</option>
                  <option value="gold">Exposição Ouro</option>
                  <option value="gold_premium">Exposição Ouro Premium</option>
                </select>
              </div>
              <p className="text-[11px] text-slate-500">A cobrança do anúncio é feita pelo Mercado Livre no meio de pagamento cadastrado na sua conta ML.</p>
              <div className="flex gap-3 pt-1">
                <button
                  type="submit" disabled={mlSaving} data-testid="ml-publish-submit"
                  className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-yellow-950 font-bold px-6 py-3 rounded-full transition-colors"
                >
                  {mlSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Publicar anúncio
                </button>
                <button type="button" onClick={() => setMlFor(null)} className="glass text-white text-sm font-semibold px-6 py-3 rounded-full">Cancelar</button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {catManager && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setCatManager(false)}
            data-testid="categorias-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-6 w-full max-w-md space-y-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-lg font-bold text-white">Categorias do estoque</h2>
                  <p className="text-xs text-slate-400">Crie, renomeie ou remova. Renomear atualiza os carros automaticamente.</p>
                </div>
                <button onClick={() => setCatManager(false)} data-testid="categorias-modal-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              <div className="flex gap-2">
                <input
                  className={inputCls} placeholder="Nova categoria (ex: Picapes)"
                  value={novaCat} onChange={(e) => setNovaCat(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCategoria())}
                  data-testid="categoria-input-nova"
                />
                <button type="button" onClick={addCategoria} data-testid="categoria-add-button" className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-sm font-bold px-4 rounded-xl transition-colors">
                  <Plus className="w-4 h-4" /> Criar
                </button>
              </div>

              <ul className="space-y-2" data-testid="categorias-list">
                {todasCategorias.map((c) => {
                  const emUso = (vehicles || []).filter((v) => v.categoria === c).length;
                  const editando = editCat?.de === c;
                  return (
                    <li key={c} className="flex items-center gap-2 bg-[#0d0f17] rounded-xl px-4 py-2.5" data-testid={`categoria-item-${c}`}>
                      {editando ? (
                        <>
                          <input
                            className={inputCls} value={editCat.para} autoFocus
                            onChange={(e) => setEditCat({ ...editCat, para: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), renomearCategoria())}
                            data-testid="categoria-input-rename"
                          />
                          <button type="button" onClick={renomearCategoria} data-testid="categoria-rename-save" className="w-9 h-9 shrink-0 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30" title="Salvar">
                            <Check className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium text-white">{c}</span>
                          <span className="text-[11px] text-slate-500 shrink-0">{emUso} carro(s)</span>
                          <button type="button" onClick={() => setEditCat({ de: c, para: c })} data-testid={`categoria-edit-${c}`} className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30" title="Renomear">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button type="button" onClick={() => removerCategoria(c)} data-testid={`categoria-delete-${c}`} className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25" title="Remover">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {qrFor && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setQrFor(null)}
            data-testid="qr-modal"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-7 w-full max-w-sm text-center space-y-4"
            >
              <div className="flex items-start justify-between">
                <div className="text-left">
                  <h2 className="font-display text-lg font-bold text-white">{qrFor.nome}</h2>
                  <p className="text-xs text-slate-400">QR Code do anúncio — aponta para a página pública do veículo</p>
                </div>
                <button onClick={() => setQrFor(null)} className="text-slate-400 hover:text-white" data-testid="qr-modal-close"><X className="w-5 h-5" /></button>
              </div>
              <div className="bg-white rounded-2xl p-4 inline-block">
                <img src={`${API}/vehicles/${qrFor.id}/qrcode.png`} alt={`QR Code ${qrFor.nome}`} className="w-52 h-52" data-testid="qr-image" />
              </div>
              <div className="flex gap-3">
                <a
                  href={`${API}/vehicles/${qrFor.id}/cartaz`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="qr-cartaz-download"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-4 py-3 rounded-full transition-colors text-sm"
                >
                  <FileDown className="w-4 h-4" /> Cartaz p/ vidro (PDF)
                </a>
                <a
                  href={`${API}/vehicles/${qrFor.id}/qrcode.png`}
                  download={`qrcode-${qrFor.id}.png`}
                  data-testid="qr-png-download"
                  className="inline-flex items-center justify-center gap-2 glass hover:border-emerald-500/50 text-white font-semibold px-4 py-3 rounded-full text-sm"
                >
                  Só o QR
                </a>
              </div>
              <p className="text-[11px] text-slate-500">O cartaz sai em folha A4 com nome do carro, preço, logo e QR grande — pronto para imprimir e colar no vidro.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
